/**
 * Determines if a value is empty according to specific rules:
 * - Empty strings and nullish values (null, undefined) are empty
 * - An array is empty if it has length 0 or all of its entries are empty
 * - An object is empty if all of its values are empty
 *
 * @param value - The value to check
 * @returns true if the value is considered empty, false otherwise
 */
export function isEmptyValue(value: unknown): boolean {
  // Handle nullish values
  if (value === null || value === undefined) {
    return true;
  }

  // Handle empty strings
  if (typeof value === 'string' && value === '') {
    return true;
  }

  // Handle arrays
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return true;
    }
    // Check if all entries are empty
    return value.every((entry) => isEmptyValue(entry));
  }

  // Handle objects
  if (typeof value === 'object' && value !== null) {
    const values = Object.values(value);
    // If object has no keys, it's empty
    if (values.length === 0) {
      return true;
    }
    // Check if all values are empty
    return values.every((val) => isEmptyValue(val));
  }

  // All other values are not empty
  return false;
}
